/** Click-outside dismisses the messaging sidecar unless it is pinned or in PiP. */
export function shouldCloseSidecarOnOutsideClick(
  pinned: boolean,
  pipActive = false,
): boolean {
  return !pinned && !pipActive;
}

export function isDocumentPictureInPictureSupported(win: object): boolean {
  return (
    'documentPictureInPicture' in win &&
    (win as { documentPictureInPicture?: unknown }).documentPictureInPicture != null
  );
}

/** Sidebar + conversation in PiP once the window is wide enough. */
export const SIDECAR_SPLIT_MIN_WIDTH = 680;

export type SidecarListMode = 'stacked' | 'split';
export const SIDECAR_LIST_MODE_KEY = 'cime.sidecar.listMode';

export function parseSidecarListMode(raw: string | null | undefined): SidecarListMode {
  return raw === 'split' ? 'split' : 'stacked';
}

export function shouldShowSidecarSplitLayout(
  pipActive: boolean,
  widthPx: number,
  listMode: SidecarListMode = 'stacked',
): boolean {
  if (listMode === 'split') return true;
  return pipActive && widthPx >= SIDECAR_SPLIT_MIN_WIDTH;
}

/** Portal only when the PiP document exists — otherwise keep the in-page panel. */
export function shouldPortalSidecarToPip(
  pipActive: boolean,
  pipBody: HTMLElement | null,
): boolean {
  return pipActive && pipBody != null;
}

/** Portal target for overlays rendered from the sidecar (lightbox, etc.). */
export function getPortalRoot(
  node: { ownerDocument?: { body: HTMLElement } | null } | null | undefined,
  fallbackDoc: { body: HTMLElement } = document,
): HTMLElement {
  return node?.ownerDocument?.body ?? fallbackDoc.body;
}

export function shouldDisableLightboxNoScroll(
  node: { ownerDocument?: object | null } | null | undefined,
  pageDoc: object = document,
): boolean {
  const doc = node?.ownerDocument;
  return !!doc && doc !== pageDoc;
}

/** Document that receives pointer/keyboard events for a node (PiP vs opener). */
export function getOwnerDocument<T>(
  node: { ownerDocument?: T | null } | null | undefined,
  fallback: T,
): T {
  return node?.ownerDocument ?? fallback;
}

export function shouldDismissOverlay<T>(
  root: { contains: (node: T) => boolean } | null,
  eventTarget: T | null | undefined,
): boolean {
  if (!root || eventTarget == null) return false;
  return !root.contains(eventTarget);
}

/** Plein écran de la fenêtre Picture-in-Picture (API Fullscreen sur son document). */
export function isPipDocumentFullscreen(
  doc: { fullscreenElement: unknown } | null | undefined,
): boolean {
  return doc != null && doc.fullscreenElement != null;
}

export function messagingPagePath(channelId?: string | null): string {
  return channelId ? `/messagerie/${channelId}` : '/messagerie';
}

export function isMessagingPagePath(pathname: string): boolean {
  return pathname === '/messagerie' || pathname.startsWith('/messagerie/');
}

export function shouldRenderMessagingSidecar(pathname: string): boolean {
  return !isMessagingPagePath(pathname);
}

export function openMessagingInNewTab(
  channelId?: string | null,
  openWindow: (url: string, target: string, features: string) => unknown = (url, target, features) =>
    window.open(url, target, features),
): void {
  openWindow(messagingPagePath(channelId), '_blank', 'noopener,noreferrer');
}
